import React, { useEffect, useRef } from 'react';
import * as Tooltip from '@radix-ui/react-tooltip';
import './DocumentOutline.css';

export interface HeadingItem {
    id: string;
    level: number;
    text: string;
    elementId?: string;
    sectionId?: string; // Associated section ID for reliable matching
    path?: string; // Hierarchical path from database (e.g., "1.2.3")
}

interface DocumentOutlineProps {
    headings: HeadingItem[];
    onHeadingClick: (headingId: string) => void;
    selectedHeadingId?: string;
    isCollapsed?: boolean;
    headingChangeMap?: Map<string, boolean>;
    isReviewMode?: boolean;
    isLoading?: boolean;
}

const DocumentOutline: React.FC<DocumentOutlineProps> = ({
    headings,
    onHeadingClick,
    selectedHeadingId,
    isCollapsed = false,
    headingChangeMap,
    isReviewMode = false,
    isLoading = false
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const headingRefs = useRef<Map<string, HTMLDivElement>>(new Map());

    // Set ref callback for headings
    const setHeadingRef = (headingId: string, element: HTMLDivElement | null) => {
        if (element) {
            headingRefs.current.set(headingId, element);
        } else {
            headingRefs.current.delete(headingId);
        }
    };

    // Scroll selected heading into view when it changes
    useEffect(() => {
        if (selectedHeadingId && !isCollapsed) {
            const headingElement = headingRefs.current.get(selectedHeadingId);
            const container = containerRef.current;
            
            if (headingElement && container) {
                // Use requestAnimationFrame and setTimeout to ensure DOM has fully updated
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        // Find the scrollable parent (editor-section-nav)
                        let scrollableParent: HTMLElement | null = null;
                        let parent = container.parentElement;
                        
                        // Look for editor-section-nav or any scrollable parent
                        while (parent) {
                            const parentStyle = window.getComputedStyle(parent);
                            const isScrollable = parentStyle.overflowY === 'auto' || 
                                                parentStyle.overflowY === 'scroll' ||
                                                parentStyle.overflow === 'auto' ||
                                                parentStyle.overflow === 'scroll';
                            
                            // Also check for editor-section-nav class
                            if (parent.classList.contains('editor-section-nav') || isScrollable) {
                                scrollableParent = parent;
                                break;
                            }
                            parent = parent.parentElement;
                        }
                        
                        if (scrollableParent) {
                            // Calculate positions relative to scrollable parent
                            const parentRect = scrollableParent.getBoundingClientRect();
                            const elementRect = headingElement.getBoundingClientRect();
                            
                            // Calculate element position within scrollable content
                            const elementTop = elementRect.top - parentRect.top + scrollableParent.scrollTop;
                            
                            // Viewport bounds
                            const viewportTop = scrollableParent.scrollTop;
                            
                            // Always scroll to center the heading in viewport for better visibility
                            // Calculate the desired scroll position to center the element
                            const elementCenter = elementTop + (elementRect.height / 2);
                            const viewportCenter = viewportTop + (scrollableParent.clientHeight / 2);
                            const scrollOffset = elementCenter - viewportCenter;
                            
                            // Smoothly scroll to center the heading
                            scrollableParent.scrollTo({
                                top: scrollableParent.scrollTop + scrollOffset,
                                behavior: 'smooth'
                            });
                        } else {
                            // Fallback: use scrollIntoView on the element itself
                            headingElement.scrollIntoView({
                                behavior: 'smooth',
                                block: 'center',
                                inline: 'nearest'
                            });
                        }
                    }, 100);
                });
            }
        }
    }, [selectedHeadingId, isCollapsed]);

    const headingStyle = (level: number) => {
        if (isCollapsed) {
            return { paddingLeft: '0px' };
        }
        const paddingLeft = level * 12;
        return { paddingLeft: `${paddingLeft}px` };
    };

    return (
        <div 
            ref={containerRef}
            className={`document-outline ${isCollapsed ? 'collapsed' : ''}`}
        >
            {isLoading ? (
                <div className="document-outline-loading">
                    <div className="document-outline-loading-spinner"></div>
                    {!isCollapsed && (
                        <p className="document-outline-loading-text">Loading headings...</p>
                    )}
                </div>
            ) : headings.length === 0 ? (
                <div className="document-outline-empty">
                    <p className="document-outline-empty-text">
                        {isCollapsed ? '' : 'Headings will appear here as you create them'}
                    </p>
                </div>
            ) : (
                <Tooltip.Provider>
                    <div className={`document-outline-list ${isCollapsed ? 'collapsed' : ''}`}>
                        {headings.map((heading) => {
                            const isActive = selectedHeadingId === heading.id;
                            return (
                            <Tooltip.Root key={heading.id}>
                                <Tooltip.Trigger asChild>
                                    <div
                                        ref={(el) => setHeadingRef(heading.id, el)}
                                        className={`document-outline-item ${
                                            isActive ? 'is-active' : ''
                                        } ${isCollapsed ? 'collapsed' : ''}`}
                                        style={headingStyle(heading.level)}
                                        onClick={() => onHeadingClick(heading.id)}
                                    >
                                        <span className="document-outline-level">
                                            {heading.path || heading.level}
                                        </span>
                                        {!isCollapsed && (
                                            <>
                                                <span className="document-outline-text">{heading.text}</span>
                                                {isReviewMode && headingChangeMap?.get(heading.id) && (
                                                    <span 
                                                        className="document-outline-change-indicator"
                                                        title="This section has review changes"
                                                    >
                                                        ●
                                                    </span>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </Tooltip.Trigger>
                                <Tooltip.Portal>
                                    <Tooltip.Content
                                        className="document-outline-tooltip-content"
                                        side="right"
                                        sideOffset={5}
                                    >
                                        {heading.text}
                                        <Tooltip.Arrow className="document-outline-tooltip-arrow" />
                                    </Tooltip.Content>
                                </Tooltip.Portal>
                            </Tooltip.Root>
                            );
                        })}
                    </div>
                </Tooltip.Provider>
            )}
        </div>
    );
};

export default DocumentOutline;

